'use client';
import { useContext } from 'react';
import { clsx } from 'clsx';
import { Icon } from 'components/UI/Icon';
import Link from 'components/UI/Link/Link';
import { AppContext } from 'context/AppContext';
import styles from './Pagination.module.scss';
import type { PaginationItemProps, PaginationProps, SubPaginationProps } from './paginationTypes';

const PaginationItem = ({ href, number, active }: PaginationItemProps) => {
  if (active) {
    return <span className={styles.active}>{number}</span>;
  }
  return (
    <Link href={href}>
      <span>{number}</span>
    </Link>
  );
};

//INFO: since the the default pagination model starts counting with 0, we need to subtract 1 from the page number for the url

const SubPagination = ({ baseUrl, currentPage, lastPage }: SubPaginationProps) => {
  const firstSubPaginationPageNumber = currentPage * 10 + 1;
  const subPaginationNumbers = Array.from({ length: 10 }, (_, index) => firstSubPaginationPageNumber + index);

  return (
    <nav aria-label="Sub Pagination">
      <ul className={styles.subPagination}>
        {subPaginationNumbers.map(
          (number) =>
            number <= lastPage && (
              <li key={`${baseUrl}-${number}`} className={styles.subItem}>
                <Link href={`${baseUrl}?page=${number - 1}`}>
                  <span>{number}</span>
                </Link>
              </li>
            )
        )}
      </ul>
    </nav>
  );
};

const Pagination = ({ currentPage, lastPage }: PaginationProps) => {
  const startingNumber = lastPage - currentPage <= 5 ? lastPage - 9 : currentPage - 4;
  const firstStartingNumber = currentPage <= 5 || lastPage <= 10 ? 1 : startingNumber;
  const paginationNumbers = Array.from(
    { length: lastPage < 10 ? lastPage : 10 },
    (_, index) => firstStartingNumber + index
  );
  const { requestPath } = useContext(AppContext);

  if (!requestPath) {
    return null;
  }

  const backUrl = currentPage != 2 ? `${requestPath}?page=${currentPage - 2}` : requestPath;

  return (
    <div>
      <nav aria-label="Pagination" className={styles.root}>
        {currentPage !== 1 && (
          <div className={clsx(styles.gridItem, styles.prevWrapper)}>
            <Link href={requestPath} aria-label="Erstes Element" className={styles.firstItemLink}>
              <Icon name="chevronLeft" size={24} />
              <Icon name="chevronLeft" size={24} />
            </Link>
            <Link href={backUrl} aria-label="Zurück">
              <Icon name="chevronLeft" size={24} />
            </Link>
          </div>
        )}
        {currentPage !== lastPage && (
          <div className={clsx(styles.gridItem, styles.nextWrapper)}>
            <Link href={`${requestPath}?page=${currentPage}`} aria-label="Weiter">
              <Icon name="chevronRight" size={24} />
            </Link>
            <Link
              href={`${requestPath}?page=${lastPage - 1}`}
              aria-label="Letztes Element"
              className={styles.lastItemLink}
            >
              <Icon name="chevronRight" size={24} />
              <Icon name="chevronRight" size={24} />
            </Link>
          </div>
        )}
        <ul className={clsx(styles.gridItem, styles.itemWrapper)}>
          {paginationNumbers.map((number) => {
            const href = number === 1 ? requestPath : `${requestPath}?page=${number - 1}`;

            return (
              <li key={href} className={clsx(styles.item, number === currentPage && styles.active)}>
                <PaginationItem href={href} number={number} active={number === currentPage} />
              </li>
            );
          })}
        </ul>
      </nav>
      {currentPage <= 9 && currentPage * 10 < lastPage && (
        <SubPagination baseUrl={requestPath} currentPage={currentPage} lastPage={lastPage} />
      )}
    </div>
  );
};

export default Pagination;
